import { useState, useEffect } from 'react';
import { meetingsApi } from '@/services/meetings';
import { adminApi } from '@/services/admin';
import { emailService } from '@/services/email';
import { MeetingWithNotes, AdminUser } from '@/types';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import toast from 'react-hot-toast';
import { Loader2, Calendar, MapPin, FileText, Mic, Image, ClipboardList, Filter, X, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { t } from '@/i18n';

const MeetingHistoryPage = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';

  const [meetings, setMeetings] = useState<MeetingWithNotes[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Filters state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('all'); // 'all' means no user filter
  const [companyName, setCompanyName] = useState('');
  const [location, setLocation] = useState('');
  const [selectedNoteTypes, setSelectedNoteTypes] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Admin users list (for user filter)
  const [users, setUsers] = useState<AdminUser[]>([]);

  // Email sending state
  const [sendingEmailForMeeting, setSendingEmailForMeeting] = useState<number | null>(null);

  // Note types for checkboxes
  const noteTypes = [
    { value: 'BUSINESS_CARD', label: t('notes.businessCard'), icon: Image },
    { value: 'VOICE', label: t('notes.voiceNote'), icon: Mic },
    { value: 'TEXT', label: t('notes.textNote'), icon: FileText },
    { value: 'HANDWRITTEN', label: t('notes.handwritten'), icon: ClipboardList },
  ];

  // Load users list for admin
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getUsers({ limit: 1000, offset: 0 });
      setUsers(response.users);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  // Polish pluralization for notes count
  const getNotesLabel = (count: number) => {
    if (count === 0) return `${count} notatek`;
    if (count === 1) return `${count} notatka`;
    if (count >= 2 && count <= 4) return `${count} notatki`;
    return `${count} notatek`;
  };

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const params: any = { limit, offset };

      // Apply filters
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (isAdmin && selectedUserId && selectedUserId !== 'all') params.user_id = parseInt(selectedUserId);
      if (companyName) params.company_name = companyName;
      if (location) params.location = location;
      if (selectedNoteTypes.length > 0) params.note_types = selectedNoteTypes.join(',');

      const response = await meetingsApi.getMeetings(params);
      setMeetings(response.meetings);
      setTotal(response.total);
    } catch (error: any) {
      toast.error(t('history.failedToLoadMeetings'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [offset, dateFrom, dateTo, selectedUserId, companyName, location, selectedNoteTypes]);

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedUserId('all');
    setCompanyName('');
    setLocation('');
    setSelectedNoteTypes([]);
    setOffset(0);
  };

  const hasActiveFilters = dateFrom || dateTo || (selectedUserId && selectedUserId !== 'all') || companyName || location || selectedNoteTypes.length > 0;

  const toggleNoteType = (type: string) => {
    setSelectedNoteTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
    setOffset(0); // Reset to first page when filter changes
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5); // HH:MM
  };

  const getNoteIcon = (noteType: string) => {
    if (!noteType) return <FileText className="h-4 w-4" />;
    switch (noteType.toUpperCase()) {
      case 'BUSINESS_CARD':
        return <Image className="h-4 w-4" />;
      case 'VOICE':
        return <Mic className="h-4 w-4" />;
      case 'TEXT':
        return <FileText className="h-4 w-4" />;
      case 'HANDWRITTEN':
        return <ClipboardList className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getNoteTypeLabel = (noteType: string) => {
    if (!noteType) return t('notes.unknown');
    switch (noteType.toUpperCase()) {
      case 'BUSINESS_CARD':
        return t('notes.businessCard');
      case 'VOICE':
        return t('notes.voiceNote');
      case 'TEXT':
        return t('notes.textNote');
      case 'HANDWRITTEN':
        return t('notes.handwritten');
      default:
        return noteType;
    }
  };

  const getStatusBadge = (status: string) => {
    if (!status) return <Badge variant="outline">{t('notes.unknown')}</Badge>;
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge variant="default">{t('notes.completed')}</Badge>;
      case 'processing':
        return <Badge variant="secondary">{t('notes.processing')}</Badge>;
      case 'pending':
        return <Badge variant="outline">{t('notes.pending')}</Badge>;
      case 'failed':
        return <Badge variant="destructive">{t('notes.failed')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const handlePreviousPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  // Check if meeting has business card note
  // Note: We can't check for email here because NotePreview doesn't include business_card data
  // The button will be shown if there's any business card note, backend will validate email presence
  const hasBusinessCardNote = (meeting: MeetingWithNotes) => {
    return meeting.notes?.some(
      note => note.note_type === 'business_card'
    );
  };

  // Send thank you email
  const handleSendThankYou = async (e: React.MouseEvent, meetingId: number) => {
    e.preventDefault(); // Prevent navigation to meeting details
    e.stopPropagation();

    if (!confirm('Czy na pewno chcesz wysłać email z podziękowaniem?')) {
      return;
    }

    try {
      setSendingEmailForMeeting(meetingId);
      await emailService.sendThankYou(meetingId);
      toast.success('✅ Email wysłany pomyślnie!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Nie udało się wysłać emaila';
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setSendingEmailForMeeting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('history.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('history.subtitle')}
          </p>
        </div>
        <Link to="/new-meeting">
          <Button>{t('dashboard.newMeeting')}</Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="bg-muted/30">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              {t('history.filters')}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Wyczyść filtry
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Ukryj' : 'Pokaż'} filtry
              </Button>
            </div>
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent className="space-y-6">
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date-from">📅 Data od</Label>
                <Input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setOffset(0);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date-to">📅 Data do</Label>
                <Input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setOffset(0);
                  }}
                />
              </div>
            </div>

            {/* Admin: User Filter */}
            {isAdmin && (
              <div className="space-y-2">
                <Label htmlFor="user-filter">👤 Użytkownik</Label>
                <Select
                  value={selectedUserId}
                  onValueChange={(value) => {
                    setSelectedUserId(value);
                    setOffset(0);
                  }}
                >
                  <SelectTrigger id="user-filter">
                    <SelectValue placeholder="Wszyscy użytkownicy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszyscy użytkownicy</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id.toString()}>
                        {u.fullName} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company-filter">🏢 Firma</Label>
              <Input
                id="company-filter"
                type="text"
                placeholder="Szukaj po nazwie firmy..."
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setOffset(0);
                }}
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location-filter">📍 Lokalizacja</Label>
              <Input
                id="location-filter"
                type="text"
                placeholder="Szukaj po lokalizacji..."
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setOffset(0);
                }}
              />
            </div>

            {/* Note Types */}
            <div className="space-y-3">
              <Label>📝 Typ notatki</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {noteTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedNoteTypes.includes(type.value);
                  return (
                    <Button
                      key={type.value}
                      variant={isSelected ? 'default' : 'outline'}
                      className="justify-start"
                      onClick={() => toggleNoteType(type.value)}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {type.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">Aktywne filtry:</p>
                <div className="flex flex-wrap gap-2">
                  {dateFrom && (
                    <Badge variant="secondary">
                      Data od: {dateFrom}
                      <button
                        className="ml-2"
                        onClick={() => setDateFrom('')}
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {dateTo && (
                    <Badge variant="secondary">
                      Data do: {dateTo}
                      <button
                        className="ml-2"
                        onClick={() => setDateTo('')}
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {isAdmin && selectedUserId && selectedUserId !== 'all' && (
                    <Badge variant="secondary">
                      Użytkownik: {users.find(u => u.id.toString() === selectedUserId)?.fullName}
                      <button
                        className="ml-2"
                        onClick={() => setSelectedUserId('all')}
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {companyName && (
                    <Badge variant="secondary">
                      Firma: {companyName}
                      <button
                        className="ml-2"
                        onClick={() => setCompanyName('')}
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {location && (
                    <Badge variant="secondary">
                      Lokalizacja: {location}
                      <button
                        className="ml-2"
                        onClick={() => setLocation('')}
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {selectedNoteTypes.map(type => (
                    <Badge key={type} variant="secondary">
                      {noteTypes.find(t => t.value === type)?.label}
                      <button
                        className="ml-2"
                        onClick={() => toggleNoteType(type)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Results Count */}
      {!loading && (
        <div className="text-sm text-muted-foreground">
          Znaleziono: <strong>{total}</strong> {total === 1 ? 'spotkanie' : total >= 2 && total <= 4 ? 'spotkania' : 'spotkań'}
        </div>
      )}

      {/* Meetings List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : meetings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{t('history.noMeetingsFound')}</p>
            {hasActiveFilters ? (
              <Button className="mt-4" variant="outline" onClick={handleClearFilters}>
                Wyczyść filtry
              </Button>
            ) : (
              <Link to="/new-meeting">
                <Button className="mt-4">{t('dashboard.createFirstMeeting')}</Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {meetings.map((meeting) => (
              <Link to={`/meeting/${meeting.meeting_id}`} key={meeting.meeting_id} className="block">
                <Card className="hover:bg-card/80 hover:shadow-lg hover:border-primary/20 transition-all cursor-pointer border-2">
                  <CardHeader>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        {/* User info for admin */}
                        {isAdmin && meeting.user && (
                          <div className="mb-2">
                            <Badge variant="outline" className="text-xs">
                              👤 {meeting.user.fullName}
                            </Badge>
                          </div>
                        )}
                        {/* Primary heading: personName + companyName (if available) */}
                        {(meeting.person_name || meeting.company_name) ? (
                          <>
                            <CardTitle className="text-xl font-bold">
                              {meeting.person_name && meeting.company_name
                                ? `${meeting.person_name} - ${meeting.company_name}`
                                : meeting.person_name || meeting.company_name}
                            </CardTitle>
                            <CardDescription className="mt-2 flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {formatDate(meeting.meeting_date)} • {formatTime(meeting.meeting_time)}
                            </CardDescription>
                          </>
                        ) : (
                          <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            {formatDate(meeting.meeting_date)}
                          </CardTitle>
                        )}
                        <CardDescription className="mt-2 flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {meeting.location}
                        </CardDescription>
                        <div className="mt-2 flex items-center gap-2">
                          <Badge variant="outline" className="whitespace-nowrap">
                            {getNotesLabel(meeting.notes?.length || 0)}
                          </Badge>
                          {hasBusinessCardNote(meeting) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleSendThankYou(e, meeting.meeting_id)}
                              disabled={sendingEmailForMeeting === meeting.meeting_id}
                              className="flex items-center gap-1"
                            >
                              {sendingEmailForMeeting === meeting.meeting_id ? (
                                <>
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Wysyłanie...
                                </>
                              ) : (
                                <>
                                  <Mail className="h-3 w-3" />
                                  Wyślij podziękowanie
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  {meeting.notes && meeting.notes.length > 0 && (
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm font-medium">{t('meeting.notes')}:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2 xl:gap-3">
                          {meeting.notes.map((note) => (
                            <div
                              key={note.note_id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex items-center gap-2">
                                {getNoteIcon(note.note_type)}
                                <span className="text-sm">
                                  {getNoteTypeLabel(note.note_type)}
                                </span>
                              </div>
                              {getStatusBadge(note.processing_status)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {total > limit && (
            <Card>
              <CardContent className="py-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
{t('history.showing')} {offset + 1} {t('history.to')} {Math.min(offset + limit, total)} {t('history.of')} {total} {t('history.meetings')}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handlePreviousPage}
                      disabled={offset === 0}
                    >
{t('history.previous')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleNextPage}
                      disabled={offset + limit >= total}
                    >
{t('history.next')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default MeetingHistoryPage;
